import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity as ActivityIcon, GitFork, Loader2, Clock } from 'lucide-react';

import { TableCell, TableRow } from '@/components/ui/table';
import { formatTime } from '@/lib/utils/date';
import { ActivityList } from './ActivityList';
import { AdjustmentActivities } from './AdjustmentActivities';
import { useTaskAdjustments } from '../hooks';
import type { Task } from '../types';

interface TaskExpandedRowProps {
  task: Task;
  colSpan: number;
  /** Current viewer's user id — enables "Finish adjustment" inline on owned rows */
  currentUserId?: number | undefined;
  /** When true, each adjustment card shows a delete button (caller-computed gate). */
  canDeleteAdjustment?: boolean | undefined;
  /** True when the caller is a global MD — may delete any adjustment, not only their own. */
  isMd?: boolean | undefined;
}

export function TaskExpandedRow({
  task,
  colSpan,
  currentUserId,
  canDeleteAdjustment,
  isMd,
}: TaskExpandedRowProps) {
  const { t } = useTranslation();
  const parentActivities = task.activities ?? [];

  // Total billable time logged on the parent (rejected entries excluded).
  const loggedTotal = parentActivities.reduce(
    (sum, a) =>
      a.status === 'REJECT'
        ? sum
        : sum + (a.userEnteredTimeInSeconds ?? a.runTimeInSeconds ?? 0),
    0,
  );

  // Only fetch adjustments when the row has any — the count is supplied by the list.
  const hasAdjustments = (task.adjustmentsCount ?? 0) > 0;
  const { data: adjustments, isLoading: isLoadingAdjustments } = useTaskAdjustments(
    hasAdjustments ? task.id : undefined,
  );
  const adjustmentList = adjustments ?? [];

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="border-t border-slate-200 bg-slate-50/60 p-0">
        <div className="space-y-6 px-6 py-5">
          {/* Parent activities */}
          <section>
            <div className="mb-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ActivityIcon className="h-4 w-4 text-slate-400" />
                <h3 className="text-[13px] font-semibold text-slate-700">
                  {t('activities')}
                </h3>
                <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                  {parentActivities.length}
                </span>
              </div>
              {loggedTotal > 0 && (
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Clock className="h-3.5 w-3.5" />
                  {formatTime(loggedTotal)}
                </div>
              )}
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <ActivityList activities={parentActivities} />
            </div>
          </section>

          {/* Adjustments — only when this task has children */}
          {hasAdjustments && (
            <section>
              <div className="mb-2.5 flex items-center gap-2">
                <GitFork className="h-4 w-4 text-blue-500" />
                <h3 className="text-[13px] font-semibold text-slate-700">
                  {t('adjustment.adjustmentsTitle')}
                </h3>
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                  {task.adjustmentsCount ?? 0}
                </span>
              </div>

              {isLoadingAdjustments ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : adjustmentList.length === 0 ? (
                <p className="text-muted-foreground px-1 py-2 text-sm">
                  {t('adjustment.noAdjustments')}
                </p>
              ) : (
                <div className="space-y-2.5">
                  {adjustmentList.map((adj) => (
                    <AdjustmentActivities
                      key={adj.id}
                      adjustment={adj}
                      currentUserId={currentUserId}
                      canDelete={canDeleteAdjustment}
                      isMd={isMd}
                      defaultExpanded={
                        currentUserId != null &&
                        adj.assignedStaffId === currentUserId
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
